import { useEffect, useState } from 'react'
import api from '../services/api'
import { useAuth } from '../context/AuthContext'
import { useAsk } from '../context/PromptProvider'
import { useTheme } from '../context/ThemeContext'
import { SHORTCUTS } from '../hooks/useKeyboardShortcuts'
import { CATEGORIES as UI_PREF_CATEGORIES, listUiPrefs, clearUiPrefs } from '../lib/uiPrefs'
import {
  twoFactorStatus, twoFactorSetup, twoFactorEnable,
  twoFactorDisable, twoFactorRegenerateBackupCodes,
  getGoogleCalStatus, startGoogleCalOAuth, disconnectGoogleCal,
  listGoogleCalendars, updateGoogleCalPrefs,
  listSessions, revokeSession, revokeOtherSessions,
  getLoginHistory,
} from '../services/api'
import HelpButton from '../components/HelpButton'
import { setDisplaySize } from '../lib/displayZoom'
import { useDisplayZoomState } from '../hooks/useDisplayZoom'

export default function Profile() {
  const { user, refreshUser } = useAuth()
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [form, setForm] = useState({ name: '', email: '', phone: '', title: '', bio: '' })
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' })
  const [passwordError, setPasswordError] = useState('')
  const [passwordSuccess, setPasswordSuccess] = useState('')
  // Feedback #830: set once the user changes their password on this page, so
  // the "set a new password" banner disappears immediately (the server has
  // already cleared must_change_password; the cached user object has not).
  const [passwordChangedHere, setPasswordChangedHere] = useState(false)

  useEffect(() => { fetchProfile() }, [])

  const fetchProfile = async () => {
    try {
      const res = await api.get('/api/profile')
      setProfile(res.data)
      setForm({
        name: res.data.name || '',
        email: res.data.email || '',
        phone: res.data.phone || '',
        title: res.data.title || '',
        bio: res.data.bio || ''
      })
    } catch (err) {
      setError('Error loading profile')
    } finally {
      setLoading(false)
    }
  }

  const handleProfileSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setSuccess('')
    try {
      const res = await api.put('/api/profile', form)
      setProfile(res.data)
      setSuccess('Profile updated successfully')
      setTimeout(() => setSuccess(''), 3000)
    } catch (err) {
      setError(err.response?.data?.error || 'Error updating profile')
    }
  }

  const handlePasswordSubmit = async (e) => {
    e.preventDefault()
    setPasswordError('')
    setPasswordSuccess('')

    if (passwordForm.new_password.length < 8) {
      setPasswordError('New password must be at least 8 characters'); return
    }
    if (!/[A-Za-z]/.test(passwordForm.new_password) || !/\d/.test(passwordForm.new_password)) {
      setPasswordError('Password must contain at least one letter and one number'); return
    }
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setPasswordError('New passwords do not match'); return
    }

    try {
      const res = await api.put('/api/profile/password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      })
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' })
      setPasswordChangedHere(true)
      // The server cleared must_change_password; refresh the cached user so the
      // banner stays gone after navigating away and /login stops routing here.
      if (refreshUser) refreshUser()
      const revoked = res.data?.sessions_revoked || 0
      setPasswordSuccess(
        revoked > 0
          ? `Password changed. ${revoked} other device${revoked === 1 ? '' : 's'} signed out.`
          : 'Password changed successfully'
      )
      setTimeout(() => setPasswordSuccess(''), 5000)
    } catch (err) {
      setPasswordError(err.response?.data?.error || 'Error changing password')
    }
  }

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <p className="text-gray-500">Loading profile...</p>
    </div>
  )

  const permissionLabels = {
    batch_invoicing: 'Batch Invoicing', billing_tab: 'Billing', calendar: 'Calendar',
    clients: 'Clients', communications: 'Communications', conflicts: 'Conflicts',
    contacts: 'Contacts', dailies: 'Dailies', dashboard: 'Dashboard',
    downloads: 'Downloads', invoices: 'Invoices', matters: 'Matters',
    receipts: 'Receipts', reports: 'Reports', settings: 'Settings',
    sol_tracker: 'SOL Tracker', tasks: 'To-Do', team: 'Team',
    time_entries: 'Time Entries', trust: 'Trust Accounting',
    payments: 'Payments', leads: 'Leads',
    can_create_clients: 'Create Clients', can_delete_clients: 'Delete Clients',
    can_create_matters: 'Create Matters', can_edit_matters: 'Edit Matters',
    can_delete_matters: 'Delete Matters',
    can_post_time: 'Post Time', can_create_invoices: 'Create Invoices',
    can_manage_trust: 'Manage Trust', can_view_all_time: 'View All Time',
    can_manage_team: 'Manage Team', can_view_costs: 'View Costs',
    can_create_costs: 'Create Costs', can_delete_costs: 'Delete Costs',
    can_create_contacts: 'Create Contacts', can_delete_contacts: 'Delete Contacts',
    can_create_sol: 'Create SOL Entries', can_delete_sol: 'Delete SOL Entries',
    can_manage_stripe: 'Manage Stripe',
    can_view_accounting: 'View Accounting', can_manage_accounting: 'Manage Accounting',
    can_post_manual_journal: 'Post Manual Journal Entries', can_view_journal_memos: 'View Journal Memos',
    can_manage_email_templates: 'Manage Email Templates',
    can_manage_email_sequences: 'Manage Email Sequences',
    can_manage_consult_outcomes: 'Manage Consult Outcomes',
    is_admin: 'Full Admin Access'
  }

  return (
    <div className="max-w-3xl mx-auto">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">My Profile<HelpButton slug="profile" /></h2>

      {/* Feedback #830 — must_change_password is why sign-in lands here.  It
          used to be silent AND permanent (changing the password never cleared
          it), so people were sent to this page at every sign-in with no idea
          why.  Say why, and point at the fix. */}
      {user?.must_change_password && !passwordChangedHere && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 mb-4 text-sm" role="status">
          <p className="font-semibold">Please set a new password</p>
          <p className="mt-1">
            Your account is set to require a new password, so you are brought to this page each time you sign in.
            Choose one under{' '}
            <button type="button" className="underline font-medium"
              onClick={() => document.getElementById('change-password')?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
              Change Password
            </button>{' '}
            below — after that, signing in takes you straight to your Dashboard.
          </p>
        </div>
      )}

      {error && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">{error}</div>}
      {success && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 mb-4 text-sm">{success}</div>}

      {/* Profile Form */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Profile Information</h3>
        <form onSubmit={handleProfileSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="profile-name" className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
            <input id="profile-name" type="text" required value={form.name}
              onChange={e => setForm({...form, name: e.target.value})}
              autoComplete="name"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label htmlFor="profile-email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input id="profile-email" type="email" required value={form.email}
              onChange={e => setForm({...form, email: e.target.value})}
              autoComplete="email"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label htmlFor="profile-phone" className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input id="profile-phone" type="tel" value={form.phone}
              onChange={e => setForm({...form, phone: e.target.value})}
              placeholder="(555) 555-5555"
              autoComplete="tel"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label htmlFor="profile-title" className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input id="profile-title" type="text" value={form.title}
              onChange={e => setForm({...form, title: e.target.value})}
              placeholder="e.g. Senior Attorney"
              autoComplete="organization-title"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="md:col-span-2">
            <label htmlFor="profile-bio" className="block text-sm font-medium text-gray-700 mb-1">Bio</label>
            <textarea id="profile-bio" value={form.bio}
              onChange={e => setForm({...form, bio: e.target.value})}
              rows={3} placeholder="Brief bio or description..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div className="md:col-span-2">
            <button type="submit"
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors">
              Save Profile
            </button>
          </div>
        </form>
      </div>

      {/* Change Password */}
      <div id="change-password" className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Change Password</h3>
        {passwordError && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 mb-4 text-sm">{passwordError}</div>}
        {passwordSuccess && <div className="bg-green-50 border border-green-200 text-green-700 rounded-lg p-3 mb-4 text-sm">{passwordSuccess}</div>}
        <form onSubmit={handlePasswordSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label htmlFor="pw-current" className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
            <input id="pw-current" type="password" required value={passwordForm.current_password}
              onChange={e => setPasswordForm({...passwordForm, current_password: e.target.value})}
              autoComplete="current-password"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label htmlFor="pw-new" className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
            <input id="pw-new" type="password" required value={passwordForm.new_password}
              onChange={e => setPasswordForm({...passwordForm, new_password: e.target.value})}
              placeholder="Min 8 characters"
              autoComplete="new-password"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label htmlFor="pw-confirm" className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
            <input id="pw-confirm" type="password" required value={passwordForm.confirm_password}
              onChange={e => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
              autoComplete="new-password"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <button type="submit"
              className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors">
              Change Password
            </button>
          </div>
        </form>
      </div>

      {/* Restart onboarding tour */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-2">👋 Onboarding Tour</h3>
        <p className="text-sm text-gray-500 mb-4">Take the quick tour again — a 60-second walkthrough of the app's main flow.</p>
        <button
          onClick={() => {
            try { localStorage.removeItem('lextrack_onboarding') } catch {}
            window.dispatchEvent(new Event('start-onboarding-tour'))
          }}
          className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-lg text-sm font-medium transition-colors">
          Restart tour
        </button>
      </div>

      {/* Two-Factor Authentication */}
      <TwoFactorSection />

      {/* Passkeys */}
      <PasskeySection />

      {/* Active Sessions */}
      <ActiveSessionsSection />

      {/* Login History */}
      <LoginHistorySection />

      {/* Google Calendar Sync */}
      <GoogleCalendarSection />

      {/* Appearance / Theme */}
      <AppearanceSection />

      {/* Reset UI preferences */}
      <ResetPrefsSection />

      {/* Role & Permissions (read only) */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Role & Permissions</h3>
        <div className="mb-4">
          <span className="text-sm text-gray-500">Role: </span>
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
            profile?.role === 'admin' ? 'bg-purple-100 text-purple-700' :
            profile?.role === 'attorney' ? 'bg-blue-100 text-blue-700' :
            'bg-gray-100 text-gray-700'
          }`}>
            {profile?.role}
          </span>
        </div>
        {profile?.role === 'admin' ? (
          <p className="text-sm text-gray-500">Admins have full access to all features.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {Object.entries(profile?.permissions || {}).filter(([, v]) => v === true).map(([key]) => (
              <span key={key} className="px-2 py-1 bg-green-50 text-green-700 rounded text-xs font-medium">
                {permissionLabels[key] || key}
              </span>
            ))}
            {Object.entries(profile?.permissions || {}).filter(([, v]) => v === true).length === 0 && (
              <p className="text-sm text-gray-400">No permissions assigned yet. Contact your administrator.</p>
            )}
          </div>
        )}
      </div>

      {/* Keyboard Shortcuts reference */}
      <ShortcutsReferenceCard />
    </div>
  )
}

// Active Sessions — lets the user see every device currently signed in as
// them and revoke any that they don't recognize.  Solves the "lost laptop"
// story: without this, killing a leaked JWT requires rotating the global
// JWT_SECRET, which nukes everyone.
function ActiveSessionsSection() {
  const ask = useAsk()
  const [sessions, setSessions] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const load = async () => {
    setLoading(true); setError('')
    try {
      const res = await listSessions()
      setSessions(res.data?.sessions || [])
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load sessions')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { load() }, [])

  const handleRevoke = async (id) => {
    if (!await ask({ type: 'confirm', title: 'Sign out this device?', message: 'Sign this device out?', confirmLabel: 'Sign out' })) return
    setBusy(true); setError('')
    try {
      await revokeSession(id)
      await load()
    } catch (err) {
      setError(err.response?.data?.error || 'Could not revoke session')
    } finally {
      setBusy(false)
    }
  }

  const handleRevokeOthers = async () => {
    const otherCount = (sessions || []).filter(s => !s.is_current).length
    if (!otherCount) return
    if (!await ask({ type: 'confirm', title: 'Sign out other devices?', message: `Sign out ${otherCount} other device${otherCount === 1 ? '' : 's'}?`, confirmLabel: 'Sign out others', danger: true })) return
    setBusy(true); setError('')
    try {
      await revokeOtherSessions()
      await load()
    } catch (err) {
      setError(err.response?.data?.error || 'Could not revoke sessions')
    } finally {
      setBusy(false)
    }
  }

  // Best-effort device parsing from user_agent.  We don't need bulletproof
  // OS detection — just enough for the user to recognize "my iPhone" vs
  // "my desktop Chrome."
  const describeDevice = (ua) => {
    if (!ua) return 'Unknown device'
    const s = ua
    let os = 'Unknown OS'
    if (/Windows NT/i.test(s)) os = 'Windows'
    else if (/Mac OS X|Macintosh/i.test(s)) os = 'macOS'
    else if (/iPhone/i.test(s)) os = 'iPhone'
    else if (/iPad/i.test(s)) os = 'iPad'
    else if (/Android/i.test(s)) os = 'Android'
    else if (/Linux/i.test(s)) os = 'Linux'
    let browser = 'Browser'
    if (/Electron/i.test(s)) browser = 'Desktop App'
    else if (/Edg\//i.test(s)) browser = 'Edge'
    else if (/Chrome\//i.test(s) && !/Chromium/i.test(s)) browser = 'Chrome'
    else if (/Firefox\//i.test(s)) browser = 'Firefox'
    else if (/Safari\//i.test(s) && !/Chrome\//i.test(s)) browser = 'Safari'
    return `${browser} on ${os}`
  }

  const fmtRelative = (iso) => {
    if (!iso) return '—'
    const then = new Date(iso).getTime()
    const now = Date.now()
    const seconds = Math.floor((now - then) / 1000)
    if (seconds < 60) return 'just now'
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`
    if (seconds < 30 * 86400) return `${Math.floor(seconds / 86400)} d ago`
    return new Date(iso).toLocaleDateString()
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">🖥️ Active Sessions</h3>
        {sessions && sessions.filter(s => !s.is_current).length > 0 && (
          <button
            onClick={handleRevokeOthers}
            disabled={busy}
            className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50">
            Sign out all other devices
          </button>
        )}
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Every device currently signed in as you.  Don't recognize one?
        Click Revoke to sign it out immediately.
      </p>
      {error && (
        <div className="mb-3 p-3 rounded bg-red-50 text-red-700 border border-red-200 text-sm">{error}</div>
      )}
      {loading && <p className="text-sm text-gray-400">Loading…</p>}
      {!loading && sessions && sessions.length === 0 && (
        <p className="text-sm text-gray-500">No active sessions.</p>
      )}
      {!loading && sessions && sessions.length > 0 && (
        <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden">
          {sessions.map(s => (
            <div key={s.id} className={`flex items-center justify-between px-4 py-3 ${s.is_current ? 'bg-green-50' : ''}`}>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800">{describeDevice(s.user_agent)}</span>
                  {s.is_current && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-green-700 bg-green-100 px-2 py-0.5 rounded-full">This device</span>
                  )}
                  {s.remembered && !s.is_current && (
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full" title="Signed in with 'Keep me signed in' — 30-day session">Remembered</span>
                  )}
                </div>
                <div className="text-xs text-gray-500 mt-0.5">
                  {s.ip_address || 'IP unknown'} · Last active {fmtRelative(s.last_active)} · Signed in {fmtRelative(s.created_at)}
                </div>
              </div>
              {!s.is_current && (
                <button
                  onClick={() => handleRevoke(s.id)}
                  disabled={busy}
                  className="ml-3 shrink-0 text-sm text-red-600 hover:text-red-800 disabled:opacity-50">
                  Revoke
                </button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// Login history — every unique (device + IP prefix) the user has ever
// signed in from.  Populated by loginNotifier.js as a side-effect of the
// new-device email notification (same table).  Distinct from Active
// Sessions in that this is HISTORICAL — a device that logged in six
// months ago and signed out still shows here.
function LoginHistorySection() {
  const [history, setHistory] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    getLoginHistory()
      .then(r => setHistory(r.data?.history || []))
      .catch(err => setError(err.response?.data?.error || 'Could not load login history'))
      .finally(() => setLoading(false))
  }, [])

  const describeDevice = (ua) => {
    if (!ua) return 'Unknown device'
    let os = 'Unknown OS'
    if (/Windows NT/i.test(ua)) os = 'Windows'
    else if (/Mac OS X|Macintosh/i.test(ua)) os = 'macOS'
    else if (/iPhone/i.test(ua)) os = 'iPhone'
    else if (/iPad/i.test(ua)) os = 'iPad'
    else if (/Android/i.test(ua)) os = 'Android'
    else if (/Linux/i.test(ua)) os = 'Linux'
    let browser = 'Browser'
    if (/Electron/i.test(ua)) browser = 'Desktop App'
    else if (/Edg\//i.test(ua)) browser = 'Edge'
    else if (/Chrome\//i.test(ua) && !/Chromium/i.test(ua)) browser = 'Chrome'
    else if (/Firefox\//i.test(ua)) browser = 'Firefox'
    else if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua)) browser = 'Safari'
    return `${browser} on ${os}`
  }

  const fmt = (iso) => {
    if (!iso) return '—'
    const d = new Date(iso)
    return d.toLocaleString()
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">📜 Login History</h3>
      <p className="text-sm text-gray-500 mb-4">
        Every unique device + network you've signed in from.  You get an
        email the first time a new device connects.  If you see a device
        or network you don't recognize, change your password and revoke
        active sessions above.
      </p>
      {error && (
        <div className="mb-3 p-3 rounded bg-red-50 text-red-700 border border-red-200 text-sm">{error}</div>
      )}
      {loading && <p className="text-sm text-gray-400">Loading…</p>}
      {!loading && history && history.length === 0 && (
        <p className="text-sm text-gray-500">No login history yet.</p>
      )}
      {!loading && history && history.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="text-left px-3 py-2">Device</th>
                <th className="text-left px-3 py-2">IP</th>
                <th className="text-left px-3 py-2">First seen</th>
                <th className="text-left px-3 py-2">Last seen</th>
                <th className="text-right px-3 py-2">Logins</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {history.map(h => (
                <tr key={h.id}>
                  <td className="px-3 py-2 text-gray-800">{describeDevice(h.user_agent)}</td>
                  <td className="px-3 py-2 text-gray-500 font-mono text-xs">{h.ip_address || '—'}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs">{fmt(h.first_seen)}</td>
                  <td className="px-3 py-2 text-gray-500 text-xs">{fmt(h.last_seen)}</td>
                  <td className="px-3 py-2 text-right text-gray-800">{h.login_count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// Passkeys — WebAuthn credentials.  A passkey is a device-bound (or
// synced-across-devices via iCloud Keychain / Google Password Manager /
// 1Password / etc.) cryptographic credential that replaces both password
// AND second factor with a single Touch-ID / Face-ID / Windows-Hello /
// YubiKey tap.  Cryptographically bound to the origin, so phishing pages
// can't harvest the credential.
function PasskeySection() {
  const ask = useAsk()
  const [status, setStatus] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [nickname, setNickname] = useState('')

  const load = async () => {
    setLoading(true); setError('')
    try {
      const r = await api.get('/api/profile/passkey/status')
      setStatus(r.data)
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load passkeys')
    } finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const enroll = async () => {
    setBusy(true); setError('')
    try {
      // Lazy-load @simplewebauthn/browser — only pulls the ~30KB module
      // for users who actually click Enroll.
      const { startRegistration } = await import('@simplewebauthn/browser')
      const opt = await api.post('/api/profile/passkey/registration-options')
      const credential = await startRegistration({ optionsJSON: opt.data.options })
      await api.post('/api/profile/passkey/register', {
        credential,
        challenge_token: opt.data.challenge_token,
        nickname: nickname.trim() || undefined,
      })
      setNickname('')
      await load()
    } catch (e) {
      // WebAuthn browser errors have their own message shape
      const msg = e.response?.data?.error
        || e.message
        || 'Passkey enrollment failed'
      // Cancelled / dismissed by user is not really an error — normalize
      if (/NotAllowedError|The operation either timed out or was not allowed/i.test(msg)) {
        setError('Enrollment cancelled')
      } else {
        setError(msg)
      }
    } finally { setBusy(false) }
  }

  const remove = async (id) => {
    if (!await ask({ type: 'confirm', title: 'Remove passkey?', message: 'Remove this passkey?  You can enroll a new one anytime.', confirmLabel: 'Remove', danger: true })) return
    setBusy(true); setError('')
    try {
      await api.delete(`/api/profile/passkey/credentials/${id}`)
      await load()
    } catch (e) {
      setError(e.response?.data?.error || 'Could not remove passkey')
    } finally { setBusy(false) }
  }

  const isSupported = typeof window !== 'undefined'
    && !!window.PublicKeyCredential
    && !!navigator.credentials

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-1">🔑 Passkeys</h3>
      <p className="text-sm text-gray-500 mb-4">
        Passkeys use your device's built-in biometrics (Touch ID / Face ID /
        Windows Hello) or a security key (YubiKey) to sign you in.  Because
        the credential is cryptographically bound to <span className="font-mono">iuris.mcmnxcloud.com</span>,
        phishing sites can't steal it.  A passkey login replaces password
        AND second factor with a single tap.
      </p>

      {!isSupported && (
        <div className="mb-3 p-3 rounded bg-yellow-50 text-yellow-800 border border-yellow-200 text-sm">
          This browser doesn't support passkeys.  Try Chrome / Edge / Safari / Firefox on a recent OS.
        </div>
      )}

      {error && (
        <div className="mb-3 p-3 rounded bg-red-50 text-red-700 border border-red-200 text-sm">{error}</div>
      )}

      {loading && <p className="text-sm text-gray-400">Loading…</p>}

      {!loading && status && (
        <>
          {status.count > 0 && (
            <div className="divide-y divide-gray-100 border border-gray-100 rounded-lg overflow-hidden mb-4">
              {(status.credentials || []).map(c => (
                <div key={c.id} className="flex items-center justify-between px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-800">{c.nickname || 'Passkey'}</span>
                      {c.backed_up && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-blue-700 bg-blue-100 px-2 py-0.5 rounded-full" title="Synced across devices via your password manager">
                          synced
                        </span>
                      )}
                      {c.device_type === 'singleDevice' && (
                        <span className="text-[10px] font-semibold uppercase tracking-wide text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full" title="Bound to this specific device">
                          single-device
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      Enrolled {new Date(c.created_at).toLocaleDateString()}
                      {c.last_used_at && ` · last used ${new Date(c.last_used_at).toLocaleDateString()}`}
                      {c.transports && c.transports.length > 0 && ` · ${c.transports.join(', ')}`}
                    </div>
                  </div>
                  <button
                    onClick={() => remove(c.id)}
                    disabled={busy}
                    className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50 ml-3 shrink-0">
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}

          {isSupported && (
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <p className="text-sm font-medium text-gray-800 mb-2">Add a new passkey</p>
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  value={nickname}
                  onChange={e => setNickname(e.target.value)}
                  placeholder="Nickname (e.g., MacBook Touch ID)"
                  maxLength={100}
                  className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm" />
                <button
                  onClick={enroll}
                  disabled={busy}
                  className="bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded text-sm font-medium disabled:opacity-60 whitespace-nowrap">
                  {busy ? 'Waiting…' : '+ Enroll Passkey'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Your browser will prompt you to use Touch ID / Face ID / Windows Hello or plug in a security key.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  )
}

// Two-Factor Authentication (TOTP) — opt-in for everyone.
// Stages: 'idle' (status load) → 'setup' (QR + first-code) → 'showCodes' (10 backup codes, one-time)
// → back to 'idle'. Disable + regenerate-codes both gated by current password.
function TwoFactorSection() {
  const [status, setStatus] = useState(null)        // { enabled, enrolled_at }
  const [loading, setLoading] = useState(true)
  const [stage, setStage] = useState('idle')        // 'idle' | 'setup' | 'showCodes' | 'disabling' | 'regenerating'
  const [setupData, setSetupData] = useState(null)  // { secret, qr_code_data_url }
  const [code, setCode] = useState('')
  const [password, setPassword] = useState('')
  const [backupCodes, setBackupCodes] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const load = async () => {
    setLoading(true)
    try { const r = await twoFactorStatus(); setStatus(r.data) }
    catch (e) { setError(e.response?.data?.error || 'Could not load 2FA status') }
    finally { setLoading(false) }
  }
  useEffect(() => { load() }, [])

  const startSetup = async () => {
    setError(''); setBusy(true)
    try {
      const r = await twoFactorSetup()
      setSetupData(r.data); setCode(''); setStage('setup')
    } catch (e) { setError(e.response?.data?.error || 'Setup failed') }
    finally { setBusy(false) }
  }

  const submitEnable = async (e) => {
    e.preventDefault(); setError(''); setBusy(true)
    try {
      const r = await twoFactorEnable(code.replace(/\s/g, ''))
      setBackupCodes(r.data.backup_codes)
      setStage('showCodes')
      load()
    } catch (e) { setError(e.response?.data?.error || 'Invalid code') }
    finally { setBusy(false) }
  }

  const submitDisable = async (e) => {
    e.preventDefault(); setError(''); setBusy(true)
    try {
      await twoFactorDisable(password)
      setPassword(''); setStage('idle'); load()
    } catch (e) { setError(e.response?.data?.error || 'Could not disable') }
    finally { setBusy(false) }
  }

  const submitRegenerate = async (e) => {
    e.preventDefault(); setError(''); setBusy(true)
    try {
      const r = await twoFactorRegenerateBackupCodes(password)
      setBackupCodes(r.data.backup_codes); setPassword(''); setStage('showCodes')
    } catch (e) { setError(e.response?.data?.error || 'Could not regenerate') }
    finally { setBusy(false) }
  }

  const cancel = () => { setStage('idle'); setCode(''); setPassword(''); setError('') }

  if (loading) return null

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mt-6">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold text-gray-800">🔐 Two-Factor Authentication</h3>
        {status?.enabled
          ? <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-medium">Enabled</span>
          : <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs font-medium">Disabled</span>}
      </div>
      <p className="text-sm text-gray-500 mb-4">
        Adds a 6-digit code from an authenticator app (Google Authenticator, Authy, 1Password, etc.) on top of your password.
        Optional but strongly recommended.
      </p>

      {error && <div className="bg-red-50 text-red-700 rounded-lg p-2 mb-3 text-sm">{error}</div>}

      {/* IDLE state */}
      {stage === 'idle' && !status?.enabled && (
        <button onClick={startSetup} disabled={busy}
          className="bg-primary-600 hover:bg-primary-700 text-white rounded-lg px-4 py-2 text-sm font-medium disabled:opacity-50">
          {busy ? 'Loading…' : 'Enable 2FA'}
        </button>
      )}
      {stage === 'idle' && status?.enabled && (
        <div className="flex flex-wrap gap-2">
          <button onClick={() => setStage('disabling')}
            className="bg-white border border-red-300 text-red-600 hover:bg-red-50 rounded-lg px-4 py-2 text-sm font-medium">
            Disable 2FA
          </button>
          <button onClick={() => setStage('regenerating')}
            className="bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg px-4 py-2 text-sm font-medium">
            Regenerate backup codes
          </button>
        </div>
      )}

      {/* SETUP state: QR + secret + first code */}
      {stage === 'setup' && setupData && (
        <form onSubmit={submitEnable} className="space-y-4">
          <ol className="list-decimal ml-5 text-sm text-gray-700 space-y-1">
            <li>Open your authenticator app and add a new account.</li>
            <li>Scan this QR code (or enter the secret manually).</li>
            <li>Enter the 6-digit code the app shows you.</li>
          </ol>
          <div className="flex flex-col sm:flex-row gap-4 items-center">
            <img src={setupData.qr_code_data_url} alt="2FA QR code"
              className="border border-gray-200 rounded-lg" width={180} height={180} />
            <div className="text-xs text-gray-600">
              <p className="font-medium mb-1">Manual entry secret:</p>
              <code className="block bg-gray-100 px-2 py-1 rounded font-mono break-all">{setupData.secret}</code>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase font-medium text-gray-500">6-digit code</label>
            <input autoFocus inputMode="numeric" pattern="[0-9 ]*"
              value={code} onChange={e => setCode(e.target.value)} required
              className="w-full mt-1 border border-gray-300 rounded-lg px-3 py-2 font-mono tracking-widest text-center"
              placeholder="123 456" />
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={cancel} disabled={busy} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={busy || !code}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm disabled:opacity-50">
              {busy ? 'Verifying…' : 'Verify & Enable'}
            </button>
          </div>
        </form>
      )}

      {/* SHOW CODES — one-time display */}
      {stage === 'showCodes' && backupCodes && (
        <div className="space-y-3">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
            <strong>Save these backup codes somewhere safe.</strong> Each works once; use them if you lose access to your authenticator app. They will <em>not</em> be shown again.
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 font-mono text-sm grid grid-cols-2 gap-1">
            {backupCodes.map((c, i) => <div key={i} className="select-all">{c}</div>)}
          </div>
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { navigator.clipboard.writeText(backupCodes.join('\n')) }}
              className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Copy all</button>
            <button type="button" onClick={() => { setBackupCodes(null); setStage('idle') }}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm">I've saved them</button>
          </div>
        </div>
      )}

      {/* DISABLING — confirm with password */}
      {stage === 'disabling' && (
        <form onSubmit={submitDisable} className="space-y-3">
          <p className="text-sm text-gray-700">Enter your current password to confirm disabling 2FA:</p>
          <input type="password" autoFocus autoComplete="current-password"
            value={password} onChange={e => setPassword(e.target.value)} required
            className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="Current password" />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={cancel} disabled={busy} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={busy || !password}
              className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg text-sm disabled:opacity-50">
              {busy ? 'Disabling…' : 'Disable 2FA'}
            </button>
          </div>
        </form>
      )}

      {/* REGENERATING — confirm with password */}
      {stage === 'regenerating' && (
        <form onSubmit={submitRegenerate} className="space-y-3">
          <p className="text-sm text-gray-700">Enter your current password to issue 10 fresh backup codes (the old ones will be invalidated):</p>
          <input type="password" autoFocus autoComplete="current-password"
            value={password} onChange={e => setPassword(e.target.value)} required
            className="w-full border border-gray-300 rounded-lg px-3 py-2" placeholder="Current password" />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={cancel} disabled={busy} className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={busy || !password}
              className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm disabled:opacity-50">
              {busy ? 'Generating…' : 'Generate new codes'}
            </button>
          </div>
        </form>
      )}
    </div>
  )
}

// ---------------- Google Calendar Sync ----------------
function GoogleCalendarSection() {
  const ask = useAsk()
  const [status, setStatus] = useState(null)
  const [calendars, setCalendars] = useState([])
  const [calendarsLoading, setCalendarsLoading] = useState(false)
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [error, setError] = useState('')

  const load = async () => {
    try {
      const r = await getGoogleCalStatus()
      setStatus(r.data)
      if (r.data.connected) {
        setCalendarsLoading(true)
        try {
          const c = await listGoogleCalendars()
          setCalendars(c.data.calendars || [])
        } catch (e) {
          // Probably revoked tokens; show but don't crash
          setError('Could not load your Google calendars — try disconnecting and reconnecting.')
        } finally {
          setCalendarsLoading(false)
        }
      }
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load Google Calendar status')
    }
  }

  useEffect(() => {
    load()
    // Surface the redirect-back messages from /oauth/callback
    const params = new URLSearchParams(window.location.search)
    const gcal = params.get('gcal')
    if (gcal === 'connected') setMsg('✓ Google Calendar connected.')
    else if (gcal === 'denied') setError('You declined the Google consent screen.')
    else if (gcal === 'bad_state') setError('Connection security check failed. Please try again.')
    else if (gcal === 'no_refresh_token') setError('Google did not issue a refresh token — disconnect first, then try again.')
    else if (gcal === 'missing_code') setError('Connection failed — no authorization code returned.')
    else if (gcal === 'error') setError('Connection failed — check the server logs or try again.')
    // Clean ?gcal=... from the URL so a refresh doesn't re-show the toast
    if (gcal) {
      const url = new URL(window.location.href)
      url.searchParams.delete('gcal')
      window.history.replaceState({}, '', url.toString())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleConnect = async () => {
    setBusy(true); setError(''); setMsg('')
    try {
      const r = await startGoogleCalOAuth()
      window.location.href = r.data.url
    } catch (e) {
      setError(e.response?.data?.error || 'Could not start Google connection')
      setBusy(false)
    }
  }
  const handleDisconnect = async () => {
    if (!await ask({ type: 'confirm', title: 'Disconnect Google Calendar?', message: 'Disconnect Google Calendar? Future Omnia events with the sync option will no longer push to your Google. Existing synced events stay in your Google calendar.', confirmLabel: 'Disconnect', danger: true })) return
    setBusy(true); setError(''); setMsg('')
    try {
      await disconnectGoogleCal()
      setMsg('✓ Disconnected.')
      setStatus({ ...status, connected: false })
      setCalendars([])
    } catch (e) {
      setError(e.response?.data?.error || 'Could not disconnect')
    } finally {
      setBusy(false)
    }
  }
  const handleSetCalendar = async (calendarId) => {
    setError(''); setMsg('')
    try {
      await updateGoogleCalPrefs({ google_calendar_id: calendarId })
      setStatus({ ...status, google_calendar_id: calendarId })
      setMsg('✓ Calendar selection saved.')
    } catch (e) {
      setError(e.response?.data?.error || 'Could not save calendar selection')
    }
  }
  const handleToggleTeamPushes = async (e) => {
    const next = e.target.checked
    setError(''); setMsg('')
    try {
      await updateGoogleCalPrefs({ accept_team_pushes: next })
      setStatus({ ...status, accept_team_pushes: next })
      setMsg(next ? '✓ Team members can now push events to your calendar.' : '✓ Team push access disabled.')
    } catch (er) {
      setError(er.response?.data?.error || 'Could not update preference')
    }
  }

  if (!status) {
    return (
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-800">📅 Google Calendar Sync</h3>
        <p className="text-sm text-gray-500 mt-2">Loading…</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-1">📅 Google Calendar Sync</h3>
      <p className="text-sm text-gray-600 mb-4">
        Connect a Google account to push your Lextrack calendar events to Google Calendar — works on your phone, computer, anywhere Google Calendar works.
      </p>

      {msg && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">{msg}</div>}
      {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">{error}</div>}

      {!status.configured && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
          ⚠️ Google Calendar integration is not configured on this server. Your administrator needs to add Google OAuth credentials to <code>.env</code> — see <code>docs/google-calendar-setup.md</code>.
        </div>
      )}

      {status.configured && !status.connected && (
        <button
          onClick={handleConnect}
          disabled={busy}
          className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
          {busy ? 'Connecting…' : 'Connect Google Calendar'}
        </button>
      )}

      {status.connected && (
        <div className="space-y-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-sm">
                <span className="text-gray-600">Connected as:</span>{' '}
                <span className="font-medium text-gray-900">{status.google_email || '(unknown email)'}</span>
              </div>
              {status.connected_at && (
                <div className="text-xs text-gray-500 mt-1">
                  Since {new Date(status.connected_at).toLocaleDateString()}
                </div>
              )}
              {status.last_sync_error && (
                <div className="text-xs text-red-600 mt-1">Last sync error: {status.last_sync_error}</div>
              )}
            </div>
            <button onClick={handleDisconnect} disabled={busy}
              className="text-red-600 hover:text-red-700 text-sm underline">
              Disconnect
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sync events to which Google calendar?</label>
            {calendarsLoading ? (
              <div className="text-sm text-gray-500">Loading calendars…</div>
            ) : (
              <select
                value={status.google_calendar_id || 'primary'}
                onChange={(e) => handleSetCalendar(e.target.value)}
                className="border border-gray-300 rounded px-3 py-2 text-sm w-full max-w-md">
                {calendars.length === 0 && <option value="primary">Primary calendar</option>}
                {calendars.map(c => (
                  <option key={c.id} value={c.id}>
                    {c.summary}{c.primary ? ' (primary)' : ''}
                  </option>
                ))}
              </select>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Events with the "Sync to Google Calendar" checkbox will land in this calendar.
            </p>
          </div>

          <div className="border-t border-gray-200 pt-4">
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!status.accept_team_pushes}
                onChange={handleToggleTeamPushes}
                className="mt-1"
              />
              <div>
                <div className="text-sm font-medium text-gray-800">
                  Allow team members to push events to my Google calendar
                </div>
                <p className="text-xs text-gray-600 mt-1">
                  When ON, colleagues creating an event in Lextrack can pick you as a recipient and the event lands directly on your Google calendar (no invitation/accept step). When OFF, only events you create yourself will sync to your Google.
                </p>
              </div>
            </label>
          </div>
        </div>
      )}
    </div>
  )
}

function ShortcutsReferenceCard() {
  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-800">⌨️ Keyboard Shortcuts</h3>
        <button onClick={() => window.dispatchEvent(new Event('show-shortcuts-help'))}
          className="text-xs text-primary-600 hover:underline">Open overlay</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm">
        {SHORTCUTS.map(group => (
          <div key={group.section}>
            <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 mb-2">{group.section}</p>
            <table className="w-full">
              <tbody>
                {group.rows.map((r, i) => (
                  <tr key={i} className="border-t border-gray-50 first:border-t-0">
                    <td className="py-1.5 pr-4 align-top">
                      <span className="flex gap-1 flex-wrap">
                        {r.keys.map((k, j) => (
                          <span key={j} className="flex items-center">
                            {j > 0 && <span className="text-gray-400 mx-1 text-xs">+</span>}
                            <kbd className="px-1.5 py-0.5 text-[11px] font-mono font-semibold bg-gray-100 border border-gray-300 rounded text-gray-700">{k}</kbd>
                          </span>
                        ))}
                      </span>
                    </td>
                    <td className="py-1.5 text-gray-700">{r.desc}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ))}
      </div>
    </div>
  )
}

// Display size (this computer) — lib/displayZoom.js.  Auto scales the app to
// the window (70-200 %; 85 % at the least on a screen scaled 150 %+); Smaller
// / Larger shift it.  Kept per person in this browser / desktop app only
// (localStorage 'lextrack_display_size:<user id>', James D3 2026-10-29),
// never on the account; the sign-in page, the portal and every other
// signed-out page use Auto (isAutoOnlyPath).
const DISPLAY_SIZE_OPTIONS = [
  { id: 'auto', label: 'Auto' },
  { id: 'smaller', label: 'Smaller' },
  { id: 'larger', label: 'Larger' },
]

// Exported for pages/Profile.displaySize.test.js.
export function AppearanceSection() {
  const t = useTheme()
  const dz = useDisplayZoomState()

  const Section = ({ title, children }) => (
    <div className="mb-6">
      <h4 className="text-sm font-semibold text-gray-700 mb-2">{title}</h4>
      {children}
    </div>
  )

  const Pill = ({ active, onClick, children, className = '', pressed }) => (
    <button type="button" onClick={onClick} aria-pressed={pressed}
      className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors border ${
        active ? 'bg-primary-600 text-white border-primary-600' : 'bg-gray-50 text-gray-700 border-gray-300 hover:bg-gray-100'
      } ${className}`}>
      {children}
    </button>
  )

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">🎨 Appearance</h3>

      <Section title="Theme">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {t.THEMES.map(th => (
            <button key={th.id} type="button" onClick={() => t.setTheme(th.id)}
              className={`p-3 rounded-xl border-2 transition-all text-left ${t.theme === th.id ? 'border-primary-600 ring-2 ring-primary-300' : 'border-gray-200 hover:border-gray-300'}`}>
              <div className="flex gap-1 mb-2">
                {th.swatches.map((c, i) => (
                  <div key={i} className="w-6 h-6 rounded border border-gray-300" style={{ background: c }} />
                ))}
              </div>
              <p className="text-sm font-medium text-gray-800">{th.label}</p>
            </button>
          ))}
        </div>
      </Section>

      <Section title="Accent Color">
        <div className="flex flex-wrap gap-3">
          {t.ACCENTS.map(a => (
            <button key={a.id} type="button" onClick={() => t.setAccentColor(a.id)}
              title={a.label}
              className={`w-8 h-8 rounded-full transition-all ${t.accent_color === a.id ? 'ring-2 ring-offset-2 ring-gray-400 scale-110' : 'hover:scale-110'}`}
              style={{ background: a.hex }} />
          ))}
        </div>
      </Section>

      <Section title="Font Size">
        <div className="flex flex-wrap gap-2">
          {['small', 'medium', 'large', 'xl'].map(s => (
            <Pill key={s} active={t.font_size === s} onClick={() => t.setFontSize(s)}>
              {s === 'xl' ? 'Extra Large' : s.charAt(0).toUpperCase() + s.slice(1)}
            </Pill>
          ))}
        </div>
      </Section>

      <Section title="Display size (this computer)">
        <div className="flex flex-wrap items-center gap-2" role="group" aria-label="Display size (this computer)">
          {DISPLAY_SIZE_OPTIONS.map(o => (
            <Pill key={o.id} active={dz.size === o.id} pressed={dz.size === o.id} onClick={() => setDisplaySize(o.id)}>
              {o.label}
            </Pill>
          ))}
          <span className="text-sm text-gray-600" data-testid="display-size-now">
            Now {Math.round((dz.zoom || 1) * 100)}%
          </span>
        </div>
        <p className="text-xs text-gray-500 mt-2 max-w-prose">
          <strong>Auto</strong> fits the app to this window: smaller on a short window (a 720p screen or a small
          window, down to 70%; with display scaling at 150% or more, in Windows or on a Mac's Retina screen, it stops at
          85%), larger on a big monitor or a tall window (1440p, 4K, or a window snapped to half a 4K screen), up to
          200%. <strong>Smaller</strong> and <strong>Larger</strong> shift it from there. Kept for you on this computer
          only: each person who signs in here has their own, a laptop and a desk monitor can differ, and the sign-in
          page, the client portal and the pages clients open from a link use Auto. Font Size above still applies on top.
          {dz.popup ? ' Pop-out windows aren\'t scaled automatically.' : ''}
          {!dz.popup && dz.pageZoom && Math.abs(dz.pageZoom - 1) > 0.001
            ? ` Your browser or app zoom (${Math.round(dz.pageZoom * 100)}%) counts toward it.`
            : ''}
        </p>
      </Section>

      <Section title="Border Radius">
        <div className="grid grid-cols-4 gap-2">
          {[
            { id: 'sharp', label: 'Sharp', r: 0 },
            { id: 'subtle', label: 'Subtle', r: 4 },
            { id: 'rounded', label: 'Rounded', r: 12 },
            { id: 'pill', label: 'Pill', r: 24 }
          ].map(o => (
            <button key={o.id} type="button" onClick={() => t.setBorderRadius(o.id)}
              className={`p-3 border-2 transition-all ${t.border_radius === o.id ? 'border-primary-600 ring-2 ring-primary-300' : 'border-gray-200 hover:border-gray-300'}`}
              style={{ borderRadius: 8 }}>
              <div className="h-8 bg-primary-600" style={{ borderRadius: o.r }} />
              <p className="text-xs text-gray-700 mt-2">{o.label}</p>
            </button>
          ))}
        </div>
      </Section>

      <Section title="Shadow Style">
        <div className="grid grid-cols-4 gap-2">
          {[
            { id: 'none',   label: 'None',   box: 'none' },
            { id: 'subtle', label: 'Subtle', box: '0 1px 2px rgba(0,0,0,0.05)' },
            { id: 'medium', label: 'Medium', box: '0 4px 6px rgba(0,0,0,0.10)' },
            { id: 'strong', label: 'Strong', box: '0 6px 12px rgba(0,0,0,0.20)' }
          ].map(o => (
            <button key={o.id} type="button" onClick={() => t.setShadowStyle(o.id)}
              className={`p-3 bg-white border-2 transition-all ${t.shadow_style === o.id ? 'border-primary-600' : 'border-gray-200 hover:border-gray-300'}`}
              style={{ borderRadius: 8 }}>
              <div className="h-8 bg-white border border-gray-200" style={{ boxShadow: o.box, borderRadius: 6 }} />
              <p className="text-xs text-gray-700 mt-2">{o.label}</p>
            </button>
          ))}
        </div>
      </Section>

      <Section title="Border Style">
        <div className="flex flex-wrap gap-2">
          {['none', 'subtle', 'normal', 'bold'].map(s => (
            <Pill key={s} active={t.border_style === s} onClick={() => t.setBorderStyle(s)}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </Pill>
          ))}
        </div>
      </Section>

      <Section title="Sidebar Style">
        <div className="grid grid-cols-3 gap-2">
          {[
            { id: 'floating', label: 'Floating', desc: 'Margined, rounded' },
            { id: 'flush',    label: 'Flush',    desc: 'Edge to edge' },
            { id: 'minimal',  label: 'Minimal',  desc: 'Icons only, expands on hover' }
          ].map(o => (
            <button key={o.id} type="button" onClick={() => t.setSidebarStyle(o.id)}
              className={`p-3 text-left border-2 rounded-xl transition-all ${t.sidebar_style === o.id ? 'border-primary-600 ring-2 ring-primary-300' : 'border-gray-200 hover:border-gray-300'}`}>
              <p className="text-sm font-medium text-gray-800">{o.label}</p>
              <p className="text-xs text-gray-400 mt-0.5">{o.desc}</p>
            </button>
          ))}
        </div>
      </Section>

      <Section title="Sidebar Width">
        <div className="flex gap-2">
          {['normal', 'wide'].map(w => (
            <Pill key={w} active={t.sidebar_width === w} onClick={() => t.setSidebarWidth(w)}>
              {w.charAt(0).toUpperCase() + w.slice(1)}
            </Pill>
          ))}
        </div>
      </Section>

      <Section title="Table Density">
        <div className="flex gap-2">
          {['comfortable', 'compact', 'cozy'].map(d => (
            <Pill key={d} active={t.table_density === d} onClick={() => t.setTableDensity(d)}>
              {d.charAt(0).toUpperCase() + d.slice(1)}
            </Pill>
          ))}
        </div>
      </Section>

      <Section title="Compact Mode">
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={!!t.compact_mode}
            onChange={e => t.setCompactMode(e.target.checked)}
            className="w-4 h-4" />
          <span className="text-sm text-gray-700">Reduce padding & spacing throughout the app</span>
        </label>
      </Section>

      <Section title="Date Format">
        <div className="flex flex-wrap gap-2">
          {['MM/DD/YYYY', 'DD/MM/YYYY', 'YYYY-MM-DD'].map(f => (
            <Pill key={f} active={t.date_format === f} onClick={() => t.setDateFormat(f)}>{f}</Pill>
          ))}
        </div>
      </Section>

      <Section title="Time Format">
        <div className="flex gap-2">
          <Pill active={t.time_format === '12h'} onClick={() => t.setTimeFormat('12h')}>12-hour (3:45 PM)</Pill>
          <Pill active={t.time_format === '24h'} onClick={() => t.setTimeFormat('24h')}>24-hour (15:45)</Pill>
        </div>
      </Section>

      {/* Live preview */}
      <div className="mt-6">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Live Preview</h4>
        <div className="p-4 border border-gray-200 rounded-xl bg-gray-50">
          <div className="bg-white rounded-lg shadow border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-bold text-gray-800">Smith v. Jones — Sample Matter</p>
              <span className="px-2 py-1 bg-primary-600 text-white rounded-full text-xs font-medium">Active</span>
            </div>
            <p className="text-xs text-gray-500 mb-3">Opened {t.formatDate(new Date())} · Last activity {t.formatTime(new Date())}</p>
            <div className="flex gap-2">
              <button type="button" className="px-3 py-1.5 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm">Primary</button>
              <button type="button" className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg text-sm">Secondary</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// Reset UI preferences — single place to wipe localStorage-persisted UI
// state (sorts, filters, tab choices, layout, recents, etc.) without
// having to know which page owns which key.  Preserves auth state
// (JWT + remember flag) so a reset doesn't sign you out.
function ResetPrefsSection () {
  const ask = useAsk()
  const [buckets, setBuckets] = useState(() => listUiPrefs())
  const [selected, setSelected] = useState(new Set())
  const [message, setMessage] = useState('')

  const refresh = () => setBuckets(listUiPrefs())

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id); else next.add(id)
      return next
    })
  }

  const doReset = async () => {
    if (selected.size === 0) return
    const hasActiveWork = selected.has('active_work') && buckets.active_work.length > 0
    const label = Array.from(selected).map(id => UI_PREF_CATEGORIES.find(c => c.id === id)?.label || id).join(', ')
    const confirmMsg = hasActiveWork
      ? `Reset: ${label}?\n\nThis will discard any in-progress timer or unsaved form drafts.  Cannot be undone.`
      : `Reset: ${label}?  Cannot be undone.`
    if (!await ask({ type: 'confirm', title: 'Reset preferences?', message: confirmMsg, confirmLabel: 'Reset', danger: hasActiveWork })) return
    const n = clearUiPrefs(Array.from(selected))
    refresh()
    setSelected(new Set())
    setMessage(`Cleared ${n} preference${n === 1 ? '' : 's'}.  Refresh the page to see all effects.`)
    setTimeout(() => setMessage(''), 8000)
  }

  const totalTracked = UI_PREF_CATEGORIES.reduce((acc, c) => acc + (buckets[c.id]?.length || 0), 0) + (buckets.other?.length || 0)

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-1">🧹 Reset UI Preferences</h3>
      <p className="text-sm text-gray-500 mb-4">
        Wipe locally-cached UI state (sort orders, filters, tab choices, recent items).  Sign-in and server-stored data are NOT affected — you stay logged in.
        {totalTracked > 0 && <> Currently storing <strong>{totalTracked}</strong> preferences across categories.</>}
      </p>

      <div className="space-y-2 mb-4">
        {UI_PREF_CATEGORIES.map(c => {
          const count = buckets[c.id]?.length || 0
          const disabled = count === 0
          return (
            <label key={c.id}
              className={`flex items-start gap-2 p-3 rounded-lg border ${disabled ? 'bg-gray-50 border-gray-100 cursor-not-allowed' : selected.has(c.id) ? 'bg-red-50 border-red-200 cursor-pointer' : 'bg-white border-gray-200 hover:bg-gray-50 cursor-pointer'}`}>
              <input type="checkbox"
                className="mt-0.5"
                checked={selected.has(c.id)}
                disabled={disabled}
                onChange={() => toggle(c.id)} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-800">{c.label}</span>
                  <span className="text-xs bg-gray-100 text-gray-600 rounded-full px-2 py-0.5">{count}</span>
                  {c.danger && <span className="text-[10px] font-semibold uppercase tracking-wider text-red-700 bg-red-100 rounded px-1.5 py-0.5">danger</span>}
                </div>
                <p className="text-xs text-gray-500 mt-0.5">{c.description}</p>
              </div>
            </label>
          )
        })}
        {buckets.other && buckets.other.length > 0 && (
          <label className={`flex items-start gap-2 p-3 rounded-lg border ${selected.has('other') ? 'bg-red-50 border-red-200 cursor-pointer' : 'bg-white border-gray-200 hover:bg-gray-50 cursor-pointer'}`}>
            <input type="checkbox"
              className="mt-0.5"
              checked={selected.has('other')}
              onChange={() => toggle('other')} />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-800">Uncategorized ({buckets.other.length})</span>
              </div>
              <p className="text-xs text-gray-500 mt-0.5">Anything not covered by the categories above — future-added preferences or leftovers from older versions.</p>
            </div>
          </label>
        )}
      </div>

      {message && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-3 mb-3 text-sm">{message}</div>
      )}

      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => setSelected(new Set(UI_PREF_CATEGORIES.filter(c => !c.danger && (buckets[c.id]?.length || 0) > 0).map(c => c.id)))}
          className="text-xs text-primary-700 hover:text-primary-800">
          Select all safe categories
        </button>
        <button
          onClick={doReset}
          disabled={selected.size === 0}
          className="text-sm px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white disabled:opacity-40 disabled:cursor-not-allowed">
          Reset {selected.size > 0 ? `${selected.size} categor${selected.size === 1 ? 'y' : 'ies'}` : ''}
        </button>
      </div>
    </div>
  )
}
